import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { auth, devMode, devUser, notSetUp, type StudioUser } from "./auth/auth";
import { AREAS, resolveProfile, type AreaId } from "./config/model";
import { PEOPLE } from "./data/sample";
import { desktop } from "./lib/desktop";
import { vera } from "./api/vera";
import { loadConsent, needsConsent, saveConsent } from "./lib/consent";
import { useM365 } from "./lib/m365";
import { makeChanges, recordArea, recordCard, setBehaviorUser } from "./lib/behavior";
import { serverHistory, syncSettings } from "./lib/history";
import { L, currentLang, languagesFor, saveLang, setCurrentLang, startLang, t, type Key, type Lang, LANG_NAME, T, Tf } from "./lib/i18n";
import { ConsentPanel } from "./components/Consent";
import { ConfirmHost } from "./components/ConfirmHost";
import { UpdateButton, useUpdate } from "./components/Update";
import { Icon, initials, Orb, StudioContext, type GoOptions, type Studio } from "./components/ui";
import { Home } from "./areas/Home";
import { Workspace } from "./areas/Workspace";
import { Files } from "./areas/Files";
import { Planner, Connections, Projects } from "./areas/Collaborate";
import { Pages, ClientSetup } from "./areas/Pages";
import { Training, Models, Engineering, Security, Operations } from "./areas/Internal";
import { Proposals } from "./areas/Proposals";
import { Workflows } from "./areas/Workflows";
import { TrialNotice } from "./components/TrialNotice";
import { Activity, Field, MarineHome, Records } from "./areas/Marine";
import { useAlerts } from "./lib/alerts";
import { Settings } from "./areas/Settings";
import { Workforce } from "./areas/Workforce";
import { setHomeUser } from "./lib/homePrefs";
import { GreetingBar } from "./components/GreetingBar";

/** The side menu's three quiet colours: work (sea blue), the field and people (sea green), setting up (sand gold). */
const NAV_GROUP: Partial<Record<string, "work" | "field" | "setup">> = {
  home: "work", workspace: "work", records: "work", proposals: "work", workflows: "work", planner: "work", files: "work", pages: "work", projects: "work",
  field: "field", activity: "field", workforce: "field",
  connections: "setup", operations: "setup", settings: "setup", setup: "setup", training: "setup", models: "setup", engineering: "setup", security: "setup",
};
import { CodePanel } from "./components/CodePanel";
import { crm, setMoney } from "./lib/crm";
import { isTeam, presence } from "./lib/presence";
import { COMPANY_CHANGED } from "./components/StaffAccess";
import { CLIENT_PACKS } from "./config/clientPacks";
import { BuildPanel } from "./components/BuildPanel";
import { HelpDock } from "./components/HelpPane";
import { FieldBar } from "./components/FieldBar";
import { useHomeScreenName } from "./lib/homeScreen";

/** Menu items that have words in lib/i18n.ts. */
const NAV_KEYS = { "nav.home": 1, "nav.workspace": 1, "nav.proposals": 1, "nav.workflows": 1, "nav.planner": 1, "nav.files": 1, "nav.connections": 1, "nav.records": 1, "nav.field": 1, "nav.activity": 1, "nav.operations": 1, "nav.settings": 1, "nav.workforce": 1 };

const VIEWS: Record<AreaId, () => ReactElement> = {
  home: Home, workspace: Workspace, proposals: Proposals, workflows: Workflows, planner: Planner, files: Files, pages: Pages, connections: Connections,
  projects: Projects, setup: ClientSetup, training: Training, models: Models, engineering: Engineering, security: Security, operations: Operations, settings: Settings, workforce: Workforce,
  records: Records, field: Field, activity: Activity,
};

export default function App() {
  const [user, setUser] = useState<StudioUser | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    auth.init().then((u) => { setUser(u); setReady(true); }).catch((e: Error) => { setError(e.message); setReady(true); });
  }, []);

  if (!ready) return <div className="signin"><Orb size={64} mode="think" /></div>;
  if (!user) return <SignIn error={error} onUser={setUser} />;
  return <Shell user={user} onUser={setUser} />;
}

function SignIn({ error, onUser }: { error: string; onUser: (u: StudioUser | null) => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(error);
  async function go(personId?: string) {
    setBusy(true); setMsg("");
    try { onUser(await auth.signIn(personId)); } catch (e) { setMsg(e instanceof Error ? e.message : "Sign-in failed. Please try again."); } finally { setBusy(false); }
  }
  return (
    <div className="signin">
      <div className="card signin-card">
        <Orb mode="breathe" />
        <h1>VERA Studio</h1>
        <p className="mute">One calm place where your team works with VERA.</p>
        {notSetUp ? (
          <div className="notice calm" style={{ textAlign: "left" }}><span style={{ fontSize: 13 }}>This copy of VERA isn't set up for sign-in yet. Please ask Elsa or Art for the team version.</span></div>
        ) : devMode ? (
          <>
            <span className="devbar">Demo · sample people, no real data</span>
            <label className="viewas" htmlFor="pick" style={{ margin: 0 }}><span>Sign in as</span></label>
            <div className="stack" style={{ width: "100%", gap: 6 }}>
              {PEOPLE.map((p) => <button key={p.id} className="btn" style={{ justifyContent: "flex-start", textAlign: "left", minWidth: 0 }} title={`${p.name} · ${p.title}`} disabled={busy} onClick={() => void go(p.id)}><span className="av" style={{ background: p.color, width: 24, height: 24, fontSize: 10 }}>{initials(p.name)}</span><span style={{ flex: "none" }}>{p.name}</span><span className="faint" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span></button>)}
            </div>
          </>
        ) : (
          <button className="btn primary" disabled={busy} onClick={() => void go()}>{busy ? "Opening Microsoft sign-in…" : "Sign in with Microsoft"}</button>
        )}
        {msg && <p style={{ color: "var(--bad)" }} role="alert">{msg}</p>}
        <p className="faint" style={{ fontSize: 12 }}>VERANeural team members, clients and partners sign in with their own work account.</p>
      </div>
    </div>
  );
}

function Shell({ user, onUser }: { user: StudioUser; onUser: (u: StudioUser | null) => void }) {
  const profile = useMemo(() => resolveProfile(user.roles), [user.roles]);
  const person = useMemo(() => PEOPLE.find((p) => p.id === user.samplePersonId) ?? null, [user.samplePersonId]);
  const initialArea = (location.hash.slice(1) as AreaId) || "home";
  const [area, setArea] = useState<AreaId>(profile?.areas.includes(initialArea) ? initialArea : "home");
  const [pending, setPending] = useState<GoOptions | null>(null);
  const [menu, setMenu] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const toastTimer = useRef<number>(0);
  const canvasRef = useRef<HTMLElement>(null);
  const [quick, setQuick] = useState(false);
  const [consent, setConsent] = useState(() => loadConsent(user.id));
  const [consentOpen, setConsentOpen] = useState(false);
  // Choices made on another device (or the web) arrive from the server before the first-time screen is considered.
  const [settingsReady, setSettingsReady] = useState(() => !serverHistory());
  setHomeUser(user.id);   // Home's choices (homePrefs) are this person's
  setBehaviorUser(user.id); // and so is what VERA learns about how they work (behavior)
  useEffect(() => {
    setConsent(loadConsent(user.id));
    let live = true;
    const done = () => { if (live) setSettingsReady(true); };
    const t = window.setTimeout(done, 5000);
    syncSettings(user.id)
      .then((changed) => { if (changed && live) { setConsent(loadConsent(user.id)); window.dispatchEvent(new Event("vera-m365")); window.dispatchEvent(new Event("vera-home-prefs")); } })
      .catch(() => { /* this device's copy is used */ })
      .finally(() => { window.clearTimeout(t); done(); });
    return () => { live = false; window.clearTimeout(t); };
  }, [user.id]);
  const firstTime = settingsReady && needsConsent(consent);
  const m365 = useM365(user.id, consent.m365);
  const update = useUpdate();
  // Screen language: Albanian first for EduTask, English for everyone else (lib/i18n.ts).
  const [lang, setLangState] = useState<Lang>(() => (profile ? startLang(user.id, profile) : "en"));
  // The left menu can shrink to icons, remembered on this computer.
  const [railMin, setRailMin] = useState(() => { try { return localStorage.getItem("vera-rail-min") === "1"; } catch { return false; } });
  // VERA Code on the right, for the VERANeural team on desktop. It stays mounted once opened, so closing it keeps the conversation.
  const [codeOpen, setCodeOpen] = useState(() => { try { return localStorage.getItem("vera-code-open") === "1"; } catch { return false; } });
  const [codeSeen, setCodeSeen] = useState(codeOpen);
  // Help, for everyone (Requests, phase 1); a workspace change approved there goes to Build to plan.
  const [helpOpen, setHelpOpen] = useState(false);
  // Help opens from Settings (it left the bottom of the menu, with Privacy & consent).
  // VERA learns how this person works: the pages they open, the Home cards they use; once a day a small change.
  useEffect(() => { recordArea(area); }, [area]);
  useEffect(() => {
    const t = window.setTimeout(() => { if (settingsReady) makeChanges(); }, 3000);
    const click = (e: MouseEvent) => { const c = (e.target as Element | null)?.closest?.("[data-card]")?.getAttribute("data-card"); if (c) recordCard(c); };
    document.addEventListener("click", click, true);
    return () => { window.clearTimeout(t); document.removeEventListener("click", click, true); };
  }, [settingsReady]);
  useEffect(() => { const on = () => setHelpOpen(true); window.addEventListener("vera-open-help", on); return () => window.removeEventListener("vera-open-help", on); }, []);
  useEffect(() => { const out = () => void auth.signOut().then(() => onUser(null)); window.addEventListener("vera-sign-out", out); return () => window.removeEventListener("vera-sign-out", out); }, [onUser]);
  const [buildStart, setBuildStart] = useState<{ text: string; n: number } | null>(null);
  const toggleCode = () => setCodeOpen((v) => { try { localStorage.setItem("vera-code-open", v ? "0" : "1"); } catch { /* ignore */ } if (!v) setCodeSeen(true); return !v; });
  // The team has both on the desktop app: VERA Code (Studio's code) and Build (HQ's lists). One dock, showing the one chosen.
  const [docked, setDocked] = useState<"code" | "build">(() => { try { return localStorage.getItem("vera-dock") === "build" ? "build" : "code"; } catch { return "code"; } });
  const openDock = (which: "code" | "build") => {
    if (codeOpen && docked === which) { toggleCode(); return; }
    setDocked(which); try { localStorage.setItem("vera-dock", which); } catch { /* ignore */ }
    if (!codeOpen) toggleCode();
  };
  // A client pack's company shows its own name (and brand, for white label), set by its owner.
  const [company, setCompany] = useState<{ name: string | null; brand: string | null } | null>(null);
  useEffect(() => {
    if (!user.roles.some((r) => r.startsWith("Studio.Client.")) || vera.mode !== "live") return;
    const load = () => { crm.company(() => auth.getToken()).then(setCompany).catch(() => {}); };
    load();
    window.addEventListener(COMPANY_CHANGED, load);
    return () => window.removeEventListener(COMPANY_CHANGED, load);
  }, [user.roles]);
  // The team's Studio tells HQ it's open, about once a minute (online now / last seen, for the founder and executives).
  useEffect(() => {
    if (vera.mode !== "live" || !isTeam(user.roles)) return;
    const checkIn = () => { presence.checkIn(() => auth.getToken()).catch(() => {}); };
    checkIn();
    const t = window.setInterval(checkIn, 60_000);
    return () => window.clearInterval(t);
  }, [user.roles]);
  const toggleRail = () => setRailMin((v) => { try { localStorage.setItem("vera-rail-min", v ? "0" : "1"); } catch { /* ignore */ } return !v; });

  // Ctrl+K (Cmd+K on Mac) asks VERA from anywhere. In Workspace it jumps to the message box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const say = document.getElementById("say");
        if (say) say.focus(); else setQuick(true);
      }
      if (e.key === "Escape") setQuick(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toast = useCallback((m: string) => { setToastMsg(m); window.clearTimeout(toastTimer.current); toastTimer.current = window.setTimeout(() => setToastMsg(""), 2800); }, []);
  const go = useCallback((a: AreaId, opts?: GoOptions) => {
    if (!profile?.areas.includes(a)) return;
    setArea(a); setPending(opts ?? null); setMenu(false);
    try { history.replaceState(null, "", `#${a}`); } catch { /* ignore */ }
    canvasRef.current?.scrollTo({ top: 0 });
  }, [profile]);

  // New HQ alerts since Activity was last opened, as a count on Activity in the menu (the VERANeural team only).
  // Time off waiting for an owner's decision, as a count on Workforce (Marine).
  const [timeOff, setTimeOff] = useState(0);
  useEffect(() => {
    if (!profile?.areas.includes("workforce") || vera.mode !== "live") return;
    let live = true;
    const load = () => void crm.list(() => auth.getToken(), "leave").then((rs) => { if (live) setTimeOff(rs.filter((r) => /^(requested|beantragt)$/i.test(String(r.values.status ?? ""))).length); }).catch(() => undefined);
    load(); const t = window.setInterval(load, 5 * 60_000);
    return () => { live = false; window.clearInterval(t); };
  }, [profile]);
  const { unread } = useAlerts(Boolean(profile && !profile.external && profile.areas.includes("activity")) && vera.mode === "live");

  // EduTask's light look also covers what opens over the app (consent, quick ask, toasts).

  if (!profile) {
    return (
      <div className="signin"><div className="card signin-card"><Orb /><h2>{T("Almost there")}</h2>
        <p className="mute">{T("You're signed in as")}{" "}{user.email}{T(", but you don't have a VERA Studio role yet. Ask Eva or Art to add you.")}</p>
        <button className="btn" onClick={() => void auth.signOut().then(() => onUser(null))}>{T("Sign out")}</button></div></div>
    );
  }

  setCurrentLang(lang);
  setMoney(CLIENT_PACKS.find((c) => c.key === profile.pack)?.currency ?? (profile.key === "client-edutask" ? "EUR" : "USD"));
  const langs = languagesFor(profile);
  const setLang = (l: Lang) => { saveLang(user.id, l); setLangState(l); };
  const ctx: Studio = { user, profile, person, sample: devMode || vera.mode === "sample", area, go, pending, clearPending: () => setPending(null), toast, getToken: () => auth.getToken(), consent, openConsent: () => setConsentOpen(true), m365, lang, setLang };
  const label = (id: AreaId, fallback: string) => ("nav." + id) in NAV_KEYS ? t(lang, ("nav." + id) as Key) : fallback;
  // Marine has its own calm home (its jobs and VERA's latest changes) and its own quieter look.
  // Customers with their own CRM (Marine, client packs like DAMA) have a calm Home with their records, and their own look.
  const crmClient = profile.crm === true;
  const View = crmClient && area === "home" ? MarineHome : VIEWS[area];
  const canCode = Boolean(desktop?.code) && !profile.external;
  // Marine's owners get Build mode instead: the same panel, for their workspace, with no code (desktop and web).
  const canBuild = crmClient && vera.mode === "live";
  // On the road: Marine people get the field bar on the phone (Today, Talk, Snap, Note) and "Marine Studio" on their home screen.
  const marine = crmClient && profile.scopes.includes("marine");
  useHomeScreenName(marine ? "marine" : "studio");
  const dockOpen = ((canCode || canBuild) && codeOpen) || helpOpen;
  const shown: "code" | "build" = canCode && canBuild ? docked : canCode ? "code" : "build";

  return (
    <StudioContext.Provider value={ctx}>
      <div className={`app ${menu ? "menu" : ""} ${railMin ? "rail-min" : ""} ${crmClient && profile.external ? "calm" : ""} ${dockOpen ? "code-open" : ""}`}>
        <aside className="rail" aria-label={T("Studio navigation")}>
          <div className="brand"><Orb mode="breathe" size={30} /><div><b>{profile.pack && company?.brand ? company.brand : T("VERA Studio")}</b><small>{desktop ? T("VERA for desktop") + `${update?.current ? ` ${update.current}` : ""}` : profile.external ? (profile.pack && company?.name ? company.name : profile.label) : "VERANeural"}</small></div>
            <button className="iconbtn ghost railtoggle" onClick={toggleRail} aria-pressed={railMin} aria-label={railMin ? L("Expand the menu", "Hape menunë") : L("Collapse the menu", "Mbyll menunë")} title={railMin ? L("Expand the menu", "Hape menunë") : L("Collapse the menu", "Mbyll menunë")}><Icon name="panel" size={17} /></button>
          </div>
          <nav className="nav">{AREAS.filter((a) => profile.areas.includes(a.id)).map((a) => (
            <button key={a.id} className={`g-${NAV_GROUP[a.id] ?? "work"}${area === a.id ? " on" : ""}`} aria-current={area === a.id ? "page" : undefined} title={railMin ? label(a.id, a.label) : undefined} onClick={() => go(a.id)}><Icon name={a.icon} /><span>{label(a.id, a.label)}</span>
              {a.id === "activity" && unread > 0 && area !== "activity" && <span className="count" aria-label={`${unread} new`}>{unread > 99 ? "99+" : unread}</span>}
              {a.id === "workforce" && timeOff > 0 && area !== "workforce" && <span className="count" aria-label={Tf("{n} time off to decide", { n: String(timeOff) })}>{timeOff}</span>}</button>))}
          </nav>
          <div className="rail-foot">
            <div className="me" title={railMin ? user.name : undefined}><span className="av" style={{ background: person?.color ?? "#6f3fd1" }}>{initials(user.name)}</span><div style={{ minWidth: 0 }}><b>{user.name}</b><small>{person?.title ?? profile.label}</small></div></div>
            {/* Privacy & consent, Help and Sign out are in Settings now (Eva: keep the bottom of the menu clear). */}
            {/* The same VERA for personal life: the consumer app, for anyone with a work account here. */}
            <a className="foot-link" href="https://app.veraneural.ai" target="_blank" rel="noopener noreferrer" title={L("VERA for your personal life, on the web and your phone", "VERA për jetën tënde personale, në web dhe në telefon")}><Icon name="heart" size={13} /><span>{L("VERA for you, personal", "VERA për ty, personale")}</span></a>
            {langs.length > 1 && <button className="btn langbtn" title={`${LANG_NAME[langs[0]]} / ${LANG_NAME[langs[1]]}`} onClick={() => setLang(lang === langs[0] ? langs[1] : langs[0])}><Icon name="globe" size={15} /><span>{LANG_NAME[lang === langs[0] ? langs[1] : langs[0]]}</span></button>}
          </div>
        </aside>
        <div className="main">
          <header className="top">
            <button className="burger" aria-label={T("Open menu")} onClick={() => setMenu((m) => !m)}><Icon name="menu" size={22} /></button>
            <label className="search topsearch" htmlFor="q"><Icon name="search" size={16} /><input id="q" placeholder={t(lang, "top.search")} autoComplete="off"
              onKeyDown={(e) => { const v = e.currentTarget.value.trim(); if (e.key === "Enter" && v) go("workspace", { ask: `Find anything about "${v}" that I can access` }); }} /></label>
            {/* No Ask VERA button up here: every page has Talk to VERA and Type, and Ctrl K still opens quick ask. */}
            {canCode && <button className={`btn codebtn ${codeOpen && shown === "code" ? "on" : ""}`} onClick={() => openDock("code")} aria-pressed={codeOpen && shown === "code"} title={T("VERA Code: change Studio's code from here")}><Icon name="code" size={16} /><span>{T("VERA Code")}</span></button>}
            {canBuild && <button className={`btn codebtn ${codeOpen && shown === "build" ? "on" : ""}`} onClick={() => openDock("build")} aria-pressed={codeOpen && shown === "build"} title={T("Build mode: change how your workspace works, in plain words")}><Icon name="sparkle" size={16} /><span>{T("Build")}</span></button>}
            {/* Time and weather, top right, on every page. */}
            {vera.mode === "live" && <GreetingBar compact />}
            <UpdateButton status={update} />
            {ctx.sample && <span className="proto" title={devMode ? T("This is the demo: sample people and sample data.") : T("VERA's full intelligence connects soon. Today she shows your real email, calendar, Teams, Planner and files, and tells you plainly what she can't do yet. Project overviews are samples.")}>{devMode ? T("Demo") : T("VERA preview")}</span>}
            {devMode && (
              <label className="viewas" htmlFor="role"><span>{T("View as")}</span>
                <select id="role" value={user.samplePersonId} onChange={(e) => { void auth.signIn(e.target.value).then((u) => { onUser(u ?? devUser(e.target.value)); setArea("home"); }); }}>
                  {PEOPLE.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.title}</option>)}
                </select>
              </label>
            )}
          </header>
          <main className="canvas" ref={canvasRef} tabIndex={-1}>
            <div className={`page ${area === "workspace" ? "wide" : ""}`} key={`${user.id}-${area}`}><TrialNotice /><View /></div>
          </main>
        </div>
        {canCode && codeSeen && <div className="codedock" hidden={helpOpen || !codeOpen || shown !== "code"}><CodePanel onClose={toggleCode} /></div>}
        {canBuild && codeSeen && <div className="codedock" hidden={helpOpen || !codeOpen || shown !== "build"}><BuildPanel onClose={toggleCode} start={buildStart} /></div>}
        {helpOpen && <div className="codedock"><HelpDock onClose={() => setHelpOpen(false)} onBuild={canBuild ? (text) => {
          setHelpOpen(false); setBuildStart({ text, n: Date.now() });
          if (!(codeOpen && docked === "build")) openDock("build");
        } : undefined} /></div>}
      </div>
      {(firstTime || consentOpen) && (
        <ConsentPanel key={user.id} initial={consent} firstTime={firstTime} userName={user.name}
          onSave={(c) => { setConsent(saveConsent(user.id, c)); setConsentOpen(false); toast(T(firstTime ? "Thank you. VERA is ready." : "Your choices are saved.")); }}
          onClose={() => setConsentOpen(false)}
          onDecline={() => void auth.signOut().then(() => onUser(null))} />
      )}
      {quick && <QuickAsk area={area} onClose={() => setQuick(false)} onAsk={(t) => { setQuick(false); go("workspace", { ask: t, fresh: true }); }} />}
      {!desktop && <ConfirmHost />}
      {!desktop && marine && vera.mode === "live" && <FieldBar />}
      <div className={`toast ${toastMsg ? "show" : ""}`} role="status" aria-live="polite">{toastMsg}</div>
    </StudioContext.Provider>
  );
}

const QUICK_HINT: Partial<Record<AreaId, string>> = {
  home: "What should I focus on today?",
  planner: "Which tasks are overdue, and who owns them?",
  files: "Summarise the latest proposal for me",
  pages: "Draft an invitation page for the investor update",
  connections: "Draft a reply to Carolyn's last email",
  projects: "What's blocking the Marine project?",
  setup: "Set up a new client workspace for DAMA",
  training: "Which training examples need Monika's review?",
  models: "Which model version is live in each workspace?",
  engineering: "What changed in staging this week?",
  security: "Who has access to what right now?",
  operations: "Summarise this week's usage for Anand",
};

const QUICK_HINT_SQ: Partial<Record<AreaId, string>> = {
  home: "Në çfarë duhet të përqendrohem sot?",
  proposals: "Cilat propozime kanë afat këtë muaj?",
  workflows: "Cila rrjedhë më ndihmon për një vlerësim?",
  files: "Më përmblidh propozimin e fundit",
  connections: "Harto një përgjigje për emailin e fundit",
};

/** A quick message to VERA from any page. The answer opens in Workspace, in the person's own history. */
function QuickAsk({ area, onClose, onAsk }: { area: AreaId; onClose: () => void; onAsk: (text: string) => void }) {
  const [t, setT] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const submit = () => { if (t.trim()) onAsk(t.trim()); };
  return (
    <div className="quick-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card quick" role="dialog" aria-modal="true" aria-label={L("Ask VERA", "Pyet VERA-n")}>
        <div className="row" style={{ gap: 10 }}><Orb size={28} mode="breathe" /><h2 style={{ fontSize: 19 }}>{L("Ask VERA", "Pyet VERA-n")}</h2></div>
        <textarea ref={ref} value={t} onChange={(e) => setT(e.target.value)} placeholder={currentLang() === "sq" ? (QUICK_HINT_SQ[area] ?? "Pyete VERA-n për çdo gjë") : (QUICK_HINT[area] ?? T("Ask VERA anything"))}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} />
        <div className="row" style={{ justifyContent: "space-between", flexWrap: "nowrap", gap: 12 }}>
          <span className="faint" style={{ fontSize: 12 }}>{L("Enter to send, Shift+Enter for a new line, Esc to close. Saved in your Workspace history.", "Enter për ta dërguar, Shift+Enter për rresht të ri, Esc për ta mbyllur. Ruhet në historikun e Hapësirës së punës.")}</span>
          <div className="row" style={{ gap: 8, flexWrap: "nowrap", flex: "none" }}><button className="btn" onClick={onClose}>{L("Cancel", "Anulo")}</button><button className="btn primary" disabled={!t.trim()} onClick={submit}>{L("Ask", "Pyet")}</button></div>
        </div>
      </div>
    </div>
  );
}
